import React, { useState, useEffect, useCallback, useRef } from "react";
import { ethers } from "ethers";
import { Helmet } from "react-helmet";
import garetto2Abi from "../../abi/garetto2_abi.json";

import Gamble from "../../components/local/Gamble";
import Gamble1 from "../../components/local/Gamble1";
import Gamble2 from "../../components/local/Gamble2";
import Airdrop from "../../components/global/Airdrop";

// import Wallet from "./components/Wallet";
import Global from "../../components/local/GettoGlobal.js";
import ChainScrollIndicator from "../../ChainScrollIndicator";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import CAROUSEL5 from "../../assets/carousel-images/CAROUSEL5.png";
import CAROUSEL6 from "../../assets/carousel-images/CAROUSEL6.png";
import "../../App.css";

// import { color } from "framer-motion";

const WSS_RPCS = [
  "wss://api.avax.network/ext/bc/C/ws",
  "wss://avalanche-c-chain-rpc.publicnode.com",
];

const CONTRACT = "0xa955e9fEecf47B7D5458Ba128D1EE46568373b41";

function GettoAvax() {
  // ================= STATE =================
  const [state, setState] = useState({
    reserve: "0",
    cycleId: "0",
    transfersCount: "0",
  });

  const [events, setEvents] = useState({
    latest: null,
    history: [],
  });

  const [status, setStatus] = useState("connecting");
  const [isLoading, setIsLoading] = useState(true);
  const swiperRef = useRef(null);
  const providerRef = useRef(null);
  const contractRef = useRef(null);

  const reconnectTimeoutRef = useRef(null);
  const reconnectRefFn = useRef(null);

  const endpointIndex = useRef(0);

  const TokenLogo = ({ size = 12 }) => (
    <img
      src="/logo.svg"
      alt="token logo"
      style={{
        width: `${size}px`,
        verticalAlign: "middle",
        marginLeft: "3px",
      }}
    />
  );

  // ================= LOAD STATE =================
  const loadState = useCallback(async (contract) => {
    try {
      const [reserve, cycleId, transfersCount] = await Promise.all([
        contract.reserve(),
        contract.cycleId(),
        contract.transfersCount(),
      ]);

      setState({
        reserve: ethers.formatEther(reserve),
        cycleId: cycleId.toString(),
        transfersCount: transfersCount.toString(),
      });
    } catch (e) {
      console.log("state error", e);
    }
  }, []);

  // ================= EVENT PARSER =================
  const parse = (log) => {
    return {
      sender: log.args?.sender,

      r1: log.args?.r1?.toString(),
      r2: log.args?.r2?.toString(),
      r3: log.args?.r3?.toString(),

      cycleId: log.args?.cycleId?.toString(),
      transfers: log.args?.transfers?.toString(),

      triggered: log.args?.triggered,

      profit: log.args?.profit ? ethers.formatEther(log.args.profit) : "0",

      conditionId: log.args?.conditionId?.toString(),

      timestamp: new Date().toISOString(),
    };
  };

  // ================= RECONNECT =================
  const reconnect = () => {
    setStatus("reconnecting");

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    endpointIndex.current++;

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectRefFn.current?.();
    }, 2000);
  };

  // ================= CONNECT =================
  const connect = useCallback(async () => {
    try {
      setStatus("connecting");

      const url = WSS_RPCS[endpointIndex.current % WSS_RPCS.length];

      const provider = new ethers.WebSocketProvider(url);
      await provider.getBlockNumber();

      const contract = new ethers.Contract(CONTRACT, garetto2Abi, provider);

      providerRef.current = provider;
      contractRef.current = contract;

      // ================= EVENTS =================
      contract.on("ReserveProcessed", (...args) => {
        const log = args[args.length - 1];
        const parsed = parse(log);

        setEvents((prev) => ({
          latest: parsed,
          history: [parsed, ...(prev.history || [])].slice(0, 50),
        }));

        loadState(contract);
      });

      // ================= WS FAIL HANDLING =================
      const ws = provider.websocket;
      if (ws) {
        ws.onclose = reconnect;
        ws.onerror = reconnect;
      }

      await loadState(contract);

      // ================= BACKFILL =================
      const latest = await provider.getBlockNumber();
      const from = Math.max(0, latest - 10000);

      const logs = await contract.queryFilter(
        contract.filters.ReserveProcessed(),
        from,
        latest,
      );

      logs.forEach((log) => {
        const parsed = parse(log);

        setEvents((prev) => ({
          latest: parsed,
          history: [parsed, ...(prev.history || [])],
        }));
      });

      setStatus("connected");
      setIsLoading(false);
    } catch (e) {
      reconnect();
    }
  }, [loadState]);

  // ================= CONNECT REF =================
  useEffect(() => {
    reconnectRefFn.current = connect;
  }, [connect]);

  // ================= INIT =================
  useEffect(() => {
    connect();

    return () => {
      try {
        contractRef.current?.removeAllListeners();
        providerRef.current?.destroy?.();

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      } catch {}
    };
  }, [connect]);

  // ================= POLLING FALLBACK =================
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!contractRef.current || !providerRef.current) return;

      try {
        const latest = await providerRef.current.getBlockNumber();
        const from = latest - 1000;

        const logs = await contractRef.current.queryFilter(
          "ReserveProcessed",
          from,
          latest,
        );

        logs.forEach((log) => {
          const parsed = parse(log);

          setEvents((prev) => ({
            latest: parsed,
            history: [parsed, ...(prev.history || [])].slice(0, 50),
          }));
        });
      } catch {}
    }, 7000);

    return () => clearInterval(interval);
  }, []);

  const carouselItems = [CAROUSEL6, CAROUSEL5];
  const EXPLORER = `https://snowtrace.io/address/${CONTRACT}`;
  // const TOTAL_SUPPLY = 10000000;
  // const percentage = (Number(state.reserve) / TOTAL_SUPPLY) * 100;

  return (
    <div className="pageContent">
      <ChainScrollIndicator chain="avax" />
      <Helmet>
        <title>Garetto (G-ETTO) - Gaming Redistributor Token</title>
        <meta
          name="Gaming Redistributor Token"
          content="New type of cryptocurrency - Garetto. The most decentralised token, with an integrated redistribution mechanism."
        />
        <meta
          name="keywords"
          content="G-ETTO, ETTO, G-ETTO, teretto, garetto, redistributor, token, crypto, DeFi, yield, blockchain, traiding"
        />
      </Helmet>

      <div className="firstBlock">
        <div className="carousel-container">
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
            onMouseLeave={() => swiperRef.current?.autoplay?.start()}
            navigation
            pagination={{ clickable: true }}
            autoplay={{
              delay: 6000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            loop
          >
            {carouselItems.map((img, i) => (
              <SwiperSlide key={i}>
                <img src={img} alt={`slide-${i}`} className="rounded-image" />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <div className="gettoGlobal" id="contractBlock">
        <Global />
      </div>
      <p>{status}</p>
      {isLoading && <p>Loading...</p>}

      {!isLoading && (
        <div className="aboutBlock">
          <div className="getto-container">
            <div className="getto-wrapper">
              <div className="gettoHeader">
                <div className="gambleRow">
                  <h4>REDISTRIBUTOR ENGINE</h4>
                </div>
                <div className="gambleRow">
                  <a
                    href={EXPLORER}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="address-link"
                    style={{
                      wordBreak: "break-all",
                      overflowWrap: "break-word",
                    }}
                  >
                    <div className="gambleRow">{CONTRACT}</div>
                  </a>
                </div>
                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h5>Cycle:</h5>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h6>{state.cycleId}</h6>
                  </div>
                </div>
                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h5>Reserve:</h5>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h6>
                      {Number(state.reserve).toFixed(8)}
                      <TokenLogo size={16} />
                    </h6>
                  </div>
                </div>
                <div className="gambleHeaderRow">
                  <div className="gambleRow">
                    <h5>Transfers:</h5>
                  </div>

                  <div className="gettoHeaderRowSub">
                    <h6>{state.transfersCount}</h6>
                  </div>
                </div>

                <div className="getto112">
                  <h4>Latest Transfer</h4>

                  {events.latest ? (
                    <>
                      <div className="gettoHeaderRow">
                        <div className="gettoHeaderRowSub">
                          <h5>Triggered: </h5>
                        </div>
                        <div className="gettoHeaderRowSub">
                          <span
                            className={
                              events.latest.triggered
                                ? "trigger-yes"
                                : "trigger-no"
                            }
                          >
                            {events.latest.triggered ? "YES" : "NO"}
                          </span>
                        </div>
                      </div>
                      <div className="gettoHeaderRow">
                        <div className="gettoHeaderRowSub">
                          <h5>Sender:</h5>
                        </div>

                        <div className="gettoHeaderRowSub">
                          <a
                            href={EXPLORER}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="address-link"
                            style={{
                              wordBreak: "break-all",
                              overflowWrap: "break-word",
                            }}
                          >
                            <div className="gambleRow">
                              {" "}
                              <p>
                                {events.latest.sender
                                  ? `${events.latest.sender.slice(0, 6)}...${events.latest.sender.slice(-4)}`
                                  : "Unknown"}
                              </p>
                            </div>
                          </a>
                        </div>
                      </div>
                      <div className="gettoHeaderRow">
                        <div className="gettoHeaderRowSub">
                          <h5>Transfers Count:</h5>
                        </div>
                        <p>{events.latest.transfers}</p>
                      </div>
                      <div className="gettoHeaderRow">
                        <div className="gettoHeaderRowSub">
                          <h5>Random Nums:</h5>
                        </div>
                        <div>
                          <p>
                            {events.latest.r1}, {events.latest.r2},{" "}
                            {events.latest.r3}
                          </p>
                        </div>
                      </div>
                      <div className="gettoHeaderRow">
                        <div className="gettoHeaderRowSub">
                          <h5>Condition:</h5>
                        </div>
                        <div className="gettoHeaderRowSub">
                          <p>{events.latest.conditionId}</p>
                        </div>
                      </div>
                      <div className="gettoHeaderRow">
                        <div className="gettoHeaderRowSub">
                          <h5>Profit:</h5>
                        </div>
                        <div className="gettoHeaderRowSub">
                          <p>
                            {Number(events.latest.profit).toFixed(1)}
                            <TokenLogo size={12} />
                          </p>
                        </div>
                      </div>
                      <div className="gettoHeaderRow">
                        <div className="gettoHeaderRowSub">
                          <h5>Time:</h5>
                        </div>
                        <div className="gettoHeaderRowSub">
                          <p>
                            {new Date(events.latest.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="gettoHeaderRowCentered">
                      <p>Recent events not found</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="gettoHeader">
                <div className="gambleRow">
                  <h4>CONDITIONS FOR REDISTRIBUTION</h4>
                </div>

                <div className="gambleRow">
                  <p>
                    <strong>CONDITION 1:</strong> While transfers count is 1 -
                    60 the random nums range is 2 - 90. If all three random
                    values are lower than the current transfer count it
                    redistributs 10% of reserve to the sender.
                  </p>
                </div>

                <div className="gambleRow">
                  <p>
                    <strong>CONDITION 2:</strong> While transfers count is 61 -
                    600 the random nums range is 2 - 900. If all three random
                    values are lower than the current transfer count it
                    redistributs 30% of reserve to the sender.
                  </p>
                </div>
                <div className="gambleRow">
                  <p>
                    <strong>CONDITION 3:</strong> While transfers count is 601 -
                    6000 the random nums range is 2 - 9000. If all three random
                    values are lower than the current transfer count it
                    redistributs 60% of reserve to the sender.
                  </p>
                </div>
                <div className="gambleRow">
                  <p>
                    <strong>CONDITION 4:</strong> While transfers count is 6001+
                    the random nums range is 2 - 90000. If all three random
                    values are lower than the current transfer count it
                    redistributs 90% of reserve to the sender.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="aboutBlock" id="gambleBlock">
        <h2>GAMBLE</h2>

        <div className="global-content2">
          <h3>TOURNAMENT SMART CONTRACTS</h3>
          <p>
            The Garetto ecosystem is further expanded through decentralized
            tournament smart contracts. Unlike traditional gambling platforms
            where users play against a centralized system designed to profit
            from player losses, these tournaments operate on a peer-to-peer
            competitive model in which participants compete directly against one
            another. While this remains a form of gambling, it is transparent,
            autonomous, and fully decentralized. The tournament system
            highlights the distinction between gambling and redistribution
            mechanics. Future developments will include larger tournament
            structures with marketplace-style seat buy/sell functionality.
          </p>
        </div>

        <Gamble />
        <Gamble1 />
        <Gamble2 />
      </div>

      <div className="aboutBlock">
        <div className="getto-wrapper" id="airdropBlock">
          <Airdrop />
        </div>
      </div>
    </div>
  );
}

export default GettoAvax;
